import { CaretDown, MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import { ClickAwayListener } from '@mui/material';
import { Button } from '@/components/TSX components';
import Input from '@/components/Input';
import { useNavigate } from 'react-router-dom';
import { useLogoStore } from '@/stores/logos';
import { useShallow } from 'zustand/react/shallow';
import { isEqual } from 'lodash';

export default function KDDropdown({
  kDData,
  setCurrKDSelected,
  currId = {},
  searchQuery,
  setSearchQuery,
  setStepperIndex,
  loading,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'dropdown-popper' : undefined;

  const navigate = useNavigate();

  const { logos } = useLogoStore(
    useShallow((state) => ({
      logos: state.logos,
    }))
  );

  function handleClick(event) {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  }

  const list =
    kDData?.filter((kd) => {
      if (searchQuery === '') return true;
      else return kd.name.toLowerCase().includes(searchQuery.toLowerCase());
    }) || [];

  return (
    <>
      <div className="flex h-full items-center">
        <button
          className={`ml-2 flex items-center gap-2 px-1 py-1.5 rounded-lg ${loading ? `text-pv-text-disabled-text cursor-not-allowed` : `active:text-pv-neutral-grey-300`}`}
          onClick={handleClick}
          disabled={loading}
        >
          <span className={`${anchorEl ? `text-pv-neutral-grey-300` : ``}`}>
            {currId?.name || 'New definition'}
          </span>
          <CaretDown
            weight="fill"
            className={`${anchorEl ? `transform -rotate-180 text-pv-neutral-grey-300` : ``}`}
            style={{
              transition: 'transform 0.3s ease',
            }}
          />
        </button>
      </div>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
      >
        {({ TransitionProps }) => (
          <ClickAwayListener
            onClickAway={() => {
              setAnchorEl(null);
            }}
          >
            <Fade {...TransitionProps} timeout={250}>
              <div
                className="bg-white flex flex-col rounded-lg border border-brand-ai-gray-400 relative h-fit max-h-60"
                style={{
                  boxShadow: `0px 8px 24px 0px rgba(0, 0, 0, 0.10)`,
                }}
              >
                <div
                  className="flex flex-col w-full h-full pt-3"
                  style={{
                    minWidth: '300px',
                  }}
                >
                  <Input
                    leftElem={<MagnifyingGlass size={16} />}
                    rightElem={
                      searchQuery.length > 0 && (
                        <XCircle
                          size={16}
                          onClick={() => {
                            setSearchQuery('');
                          }}
                          className="mr-2 text-pv-neutral-grey-500 cursor-pointer hover:text-pv-text-primary-text"
                        />
                      )
                    }
                    className={{
                      input: {
                        wrapper: 'pr-0 py-2 m-0 mx-3',
                      },
                    }}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    iconClassName="left-0 pl-4"
                    placeholder={`Search`}
                  />
                  <div className="flex flex-col h-full overflow-y-scroll my-1 max-h-44">
                    {searchQuery.length > 0 && (
                      <span className="py-1.5 ml-3 text-xs text-pv-text-secondary-text">
                        Search results for{' '}
                        <span className="font-medium">"{searchQuery}"</span>
                      </span>
                    )}
                    {list.length > 0 ? (
                      (isEqual(currId, {})
                        ? list
                        : [
                            currId,
                            ...list.filter((lst) => lst._id !== currId._id),
                          ]
                      ).map((kd, ind) => {
                        return (
                          <span
                            className={`px-4 py-3 cursor-pointer hover:bg-pv-primary-primary-50 border-l-3 border-transparent ${currId._id === kd._id ? `font-medium bg-pv-primary-primary-50 border-pv-primary-primary-500` : ``}`}
                            key={ind}
                            onClick={() => {
                              setSearchQuery('');
                              navigate(
                                `/data-catalog/key-definition/${kd._id}`,
                                {
                                  state: {
                                    metricInfo: kd,
                                    log: logos,
                                  },
                                }
                              );
                              setAnchorEl(null);
                            }}
                          >
                            {kd.name}
                          </span>
                        );
                      })
                    ) : (
                      <div className="w-fit ml-3 mb-2">
                        <Button
                          btnColor="secondary"
                          btnSize="sm"
                          onClick={() => {
                            setSearchQuery('');
                          }}
                        >
                          Clear Search
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
